/**
 * Cross-highlight for size-guide (and similar) tables.
 *
 * On hovering a cell it highlights three regions that plain CSS can't express
 * together (CSS has no "same column index across rows" relationship):
 *  - an "emphasis" axis  -> bg-brand-primary + text-brand-secondary (black)
 *  - a "muted" axis      -> bg-brand-tertiary
 *  - the muted axis head -> bg-borders-disabled (overrides the muted bg)
 *
 * Which axis gets which treatment depends on the layout:
 *  - Rows layout (no <thead>): the COLUMN is the emphasis (black) axis and the
 *    ROW is muted. The muted head is the row's own label cell (column 0), so
 *    the emphasis highlight is skipped when that label cell is hovered.
 *  - Column layout (has <thead>): reversed — the ROW is the emphasis (black)
 *    axis and the COLUMN is muted. The muted head is the column heading in
 *    <thead>. Emphasis is applied per-cell so it wins at the intersection.
 *
 * Uses `!` important utilities so the highlight beats the table's own cell
 * backgrounds (zebra striping, header tint) without a specificity battle.
 *
 * Class strings are written in full so Tailwind's JIT scanner picks them up.
 */
export class TableHoverElement extends HTMLElement {
  static EMPHASIS_CLASSES = ['!bg-brand-primary', '!text-brand-secondary']
  static MUTED_CLASSES = ['!bg-brand-tertiary']
  static HEADER_CLASSES = ['!bg-borders-disabled']

  private table: HTMLTableElement | null = null
  private styled: HTMLElement[] = []
  private readonly boundOver = (event: MouseEvent) => this.handleOver(event)
  private readonly boundLeave = () => this.clear()

  connectedCallback() {
    this.table = this.querySelector('table')
    if (!this.table) return
    this.table.addEventListener('mouseover', this.boundOver)
    this.table.addEventListener('mouseleave', this.boundLeave)
  }

  disconnectedCallback() {
    this.table?.removeEventListener('mouseover', this.boundOver)
    this.table?.removeEventListener('mouseleave', this.boundLeave)
    this.clear()
  }

  private handleOver(event: MouseEvent) {
    const target = event.target as HTMLElement | null
    const cell = target?.closest('td, th') as HTMLTableCellElement | null
    if (!cell || !this.table?.contains(cell)) return

    const row = cell.closest('tr')
    if (!row) return

    this.clear()

    if (this.table.tHead) {
      this.highlightColumnLayout(row, cell.cellIndex)
    } else {
      this.highlightRowsLayout(row, cell.cellIndex)
    }
  }

  /** Rows layout: column = emphasis (black), row = muted, head = row label. */
  private highlightRowsLayout(row: HTMLTableRowElement, columnIndex: number) {
    this.apply(row, TableHoverElement.MUTED_CLASSES)

    const header = row.cells[0]
    if (header) this.apply(header, TableHoverElement.HEADER_CLASSES)

    if (columnIndex > 0) {
      this.applyColumn(columnIndex, TableHoverElement.EMPHASIS_CLASSES)
    }
  }

  /** Column layout (reversed): row = emphasis (black), column = muted. */
  private highlightColumnLayout(row: HTMLTableRowElement, columnIndex: number) {
    for (const cell of Array.from(row.cells)) {
      this.apply(cell, TableHoverElement.EMPHASIS_CLASSES)
    }

    this.applyColumn(columnIndex, TableHoverElement.MUTED_CLASSES, row)

    const header = this.table?.tHead?.rows[0]?.cells[columnIndex]
    if (header) this.apply(header, TableHoverElement.HEADER_CLASSES)
  }

  private applyColumn(
    columnIndex: number,
    classes: string[],
    skipRow: HTMLTableRowElement | null = null
  ) {
    if (!this.table) return
    for (const body of Array.from(this.table.tBodies)) {
      for (const tr of Array.from(body.rows)) {
        if (tr === skipRow) continue
        const cell = tr.cells[columnIndex]
        if (cell) this.apply(cell, classes)
      }
    }
  }

  private apply(el: HTMLElement, classes: string[]) {
    el.classList.add(...classes)
    this.styled.push(el)
  }

  private clear() {
    for (const el of this.styled) {
      el.classList.remove(
        ...TableHoverElement.EMPHASIS_CLASSES,
        ...TableHoverElement.MUTED_CLASSES,
        ...TableHoverElement.HEADER_CLASSES
      )
    }
    this.styled = []
  }
}
